import { bindable, inject } from 'aurelia';
import { CustomIntervalEntity } from '../../apis/custom-interval/custom-interval-entity';
import { ScheduleViewModel } from './schedule-view-model';
import { SerieSettingEntity } from '../../apis/serie/serie-setting-entity';
import { ScheduleRowEntity } from '../../apis/schedule/schedule-row-entity';
import { OpponentsViewModel } from './opponents-view-model';
import { EventAggregator } from '@aurelia/kernel';
import { TeamSettingEntity } from '../../apis/team/team-setting-entity';
import { VenueEntity } from '../../apis/venue/venue-entity';
import { GroupEntity } from '../../apis/group/group-entity';
import { TeamGame } from './team-game';
import { PresentScheduleViewModel } from './present-schedule-view-model';
import { GroupColorScheme } from './group-color-scheme';
import { GameRow } from './game-row';
import { VenueModel } from './venue-model';
import { ScheduleFieldEntity } from '../../apis/schedule/schedule-field-entity';
import teamGameView from './team-game.html?raw';
import teamGameReadOnlyView from './team-game-read-only.html?raw';
import removedTeamView from './removed-team.html?raw';

@inject(EventAggregator)
export class SchedulePresenter {
    @bindable public games: ScheduleRowEntity[] = [];
    @bindable public serie: SerieSettingEntity = null;
    @bindable public customIntervals: CustomIntervalEntity[] = [];
    @bindable public teamSettings: TeamSettingEntity[] = [];
    @bindable public venues: VenueEntity[] = [];
    @bindable public fields: ScheduleFieldEntity[] = [];
    @bindable public groups: GroupEntity[] = [];
    // Opt-in: colours the games per group and shows a legend (used by the review page, not the public view).
    @bindable public showGroupColors: boolean = false;
    @bindable public scheduleid: string = null;
    @bindable public showDetails: boolean = true;
    @bindable public scheduleCode: string = '';
    @bindable public readonly: boolean = true;
    @bindable public getEditedScheduleRows: () => ScheduleRowEntity[];
    public opponentsVisible: boolean = false;
    public scheduleVisible: boolean = true;
    public bindingDone: boolean = false;
    public viewModel: ScheduleViewModel | PresentScheduleViewModel = null;
    public groupColorScheme: GroupColorScheme = new GroupColorScheme();
    public removedTeamView: string = removedTeamView;
    private _eventAggregator: EventAggregator;

    public get gameView(): string {
        if (this.readonly === false) {
            return teamGameView;
        }
        return teamGameReadOnlyView;
    }

    constructor(eventAggregator: EventAggregator) {
        this._eventAggregator = eventAggregator;
        this.viewModel = new ScheduleViewModel(eventAggregator);
        this.getEditedScheduleRows = () => this._getEditedScheduleRows();
    }

    public attached(): void {
        this.bindingDone = true;
        this.setupSchedule();
    }
    public gamesChanged(): void {
        if (this.bindingDone) {
            this.setupSchedule();
        }
    }

    public serieChanged(): void {
        if (this.bindingDone) {
            this.setupSchedule();
        }
    }
    public customIntervalsChanged(): void {
        if (this.bindingDone) {
            this.setupSchedule();
        }
    }

    public venuesChanged(): void {
        if (this.bindingDone) {
            this.setupSchedule();
        }
    }

    public fieldsChanged(): void {
        if (this.bindingDone) {
            this.setupSchedule();
        }
    }

    public teamSettingsChanged(): void {
        this._updateGroupColorScheme();
    }

    public groupsChanged(): void {
        this._updateGroupColorScheme();
    }

    public showGroupColorsChanged(): void {
        this._updateGroupColorScheme();
    }

    // The style is passed the scheme as an argument so the binding re-evaluates when it is replaced.
    public groupStyle(teamGame: TeamGame | null, colorScheme: GroupColorScheme): string {
        if (!this.showGroupColors || teamGame == null) {
            return '';
        }
        return `background-color:${colorScheme.getColor(teamGame.groupId)};border-bottom:1px solid rgba(0, 0, 0, 0.18)`;
    }

    public venueGameStyle(gameRow: GameRow, venue: VenueModel, colorScheme: GroupColorScheme): string {
        return this.groupStyle(this.getVenueGame(gameRow, venue), colorScheme);
    }

    public get opponentList(): OpponentsViewModel[] {
        return this.viewModel.opponentList;
    }

    public get visibleVenueModels(): VenueModel[] {
        const venueModels = this.viewModel?.venueModels ?? [];
        if (!venueModels.length || !this.games?.length) {
            return venueModels;
        }
        return venueModels.filter(venue => this.games.some(g => g.fieldId === venue.fieldId && (g.homeTeam !== '' || g.opponentTeam !== '')));
    }

    public getVenueGame(gameRow: GameRow, venue: VenueModel): TeamGame | null {
        if (!gameRow || !venue) {
            return null;
        }
        return gameRow.games.firstOrDefault(g => g.fieldId === venue.fieldId) ?? null;
    }

    public getVenueName(teamGame: TeamGame): string {
        const fieldEntry = this.fields?.find(f => f.fieldNumber === teamGame.game.fieldId);
        if (fieldEntry?.venueId != null) {
            const venue = this.venues?.firstOrDefault(v => v.id === fieldEntry.venueId);
            return venue != null ? venue.name : fieldEntry.venueId;
        }
        return teamGame.game.fieldId != null ? teamGame.game.fieldId.toString() : '';
    }
    public addTeam(team: TeamSettingEntity): void {
        if (!(this.viewModel instanceof ScheduleViewModel)) {
            return;
        }
        this.viewModel.addExtraGame(team);
        this.setupSchedule();
    }

    public showOpponents(): void {
        this.scheduleVisible = false;
        this.opponentsVisible = true;
    }
    public showSchedule(): void {
        this.opponentsVisible = false;
        this.scheduleVisible = true;
    }

    public setupSchedule(): void {
        this._updateGroupColorScheme();
        if (!this.games || !this.serie || !this.customIntervals) {
            return;
        }

        if (this.readonly) {
            this.viewModel = new PresentScheduleViewModel();
            this.viewModel.initialize(this.games, this.serie, this.customIntervals, this.venues || [], this.fields || []);
            return;
        }

        if (!(this.viewModel instanceof ScheduleViewModel)) {
            this.viewModel = new ScheduleViewModel(this._eventAggregator);
        }

        this.viewModel.initialize(this.games, this.serie, this.customIntervals, this.teamSettings, this.venues, this.fields || [], this.readonly);
    }

    public onDeleteRow(_index: number): void {
        if (!(this.viewModel instanceof ScheduleViewModel)) {
            return;
        }
        this.viewModel.deleteLastRow();
    // // const gamesToRemove = this.games.where(g => g.interval === index);
    // const interval = this.games.lastOrDefault().interval;
    // const gamesToRemove = this.games.where(g => g.interval === interval);
    // gamesToRemove.forEach(g => this.games.remove(g));
    // this.viewModel.initialize(this.games, this.serie, this.customIntervals, this.teamSettings, this.venues, this.readonly);
    }
    public onAddRow(): void {
        if (!(this.viewModel instanceof ScheduleViewModel)) {
            return;
        }
        this.viewModel.addRow();
    // const interval = this.games.lastOrDefault().interval + 1;
    // //for (let field = 1; field <= this.viewModel.fields; field++) {
    // for (const venue of this.viewModel.venueModels) {
    //   const game = new ScheduleRowEntity();
    //   game.fieldId = venue.fieldId;
    //   game.venueId = venue.venueId;
    //   game.homeTeam = ScheduleViewModel.freeSlotTeamName;
    //   game.interval = interval;
    //   game.opponentTeam = ScheduleViewModel.freeSlotTeamName;
    //   this.games.add(game);
    // }
    // this.viewModel.initialize(this.games, this.serie, this.customIntervals, this.teamSettings, this.venues, this.readonly);
    }

    private _updateGroupColorScheme(): void {
        this.groupColorScheme = this.showGroupColors
            ? new GroupColorScheme(this.teamSettings, this.groups)
            : new GroupColorScheme();
    }

    private _getEditedScheduleRows(): ScheduleRowEntity[] {
        if (!(this.viewModel instanceof ScheduleViewModel)) {
            return [];
        }
        return this.viewModel.getEditedScheduleRows();
    }
}
